import { ChangeEvent, ReactNode, useState } from 'react'

import { MENU_MAX_HEIGHT } from '../constants'
import { PickerItem, PickerValue } from '../types'
import { MenuRow } from '../utils/getGroupedMenuRows'
import { useClassNames } from '../utils/useClassNames'
import { DropdownMenuCheckItem } from './DropdownMenuCheckItem'
import { DropdownMenuGroup } from './DropdownMenuGroup'

export interface MenuItemSelection<Item extends PickerItem> {
  value: Item['value']
  item: Item
  checked: boolean
  event: ChangeEvent<HTMLInputElement>
}

interface DropdownMenuProps<Item extends PickerItem> {
  rows: MenuRow<Item>[]
  grouped: boolean
  activeValues: PickerValue[]
  focusItemValue: PickerValue | null | undefined
  renderMenuItem?: (label: ReactNode, item: Item) => ReactNode
  renderMenuGroup?: (title: ReactNode) => ReactNode
  onSelect: (selection: MenuItemSelection<Item>) => void
}

const MENU_STYLE = { maxHeight: MENU_MAX_HEIGHT }

export const DropdownMenu = <Item extends PickerItem>({
  rows,
  grouped,
  activeValues,
  focusItemValue,
  renderMenuItem,
  renderMenuGroup,
  onSelect,
}: DropdownMenuProps<Item>) => {
  const { withClassPrefix } = useClassNames('picker-check-menu')
  const [foldedGroupTitles, setFoldedGroupTitles] = useState<string[]>([])

  const toggleGroup = (title: string) => {
    const nextFoldedTitles = foldedGroupTitles.filter(foldedTitle => foldedTitle !== title)
    if (nextFoldedTitles.length === foldedGroupTitles.length) {
      nextFoldedTitles.push(title)
    }
    setFoldedGroupTitles(nextFoldedTitles)
  }

  const visibleRows = grouped
    ? rows.filter(
        row =>
          row.kind === 'group' ||
          row.groupTitle === undefined ||
          !foldedGroupTitles.includes(row.groupTitle),
      )
    : rows

  const renderRow = (row: MenuRow<Item>) => {
    if (row.kind === 'group') {
      return (
        <DropdownMenuGroup
          key={`group-${row.title}`}
          folded={foldedGroupTitles.includes(row.title)}
          onClick={() => toggleGroup(row.title)}
        >
          {renderMenuGroup ? renderMenuGroup(row.title) : row.title}
        </DropdownMenuGroup>
      )
    }

    const { item } = row
    if (item.label === undefined) {
      throw new Error(`labelKey "label" is not defined in "data" : ${item.value}`)
    }

    return (
      <DropdownMenuCheckItem
        key={item.value}
        active={activeValues.some(activeValue => Object.is(activeValue, item.value))}
        focus={focusItemValue !== undefined && Object.is(focusItemValue, item.value)}
        value={item.value}
        onSelect={({ checked, event }) => onSelect({ value: item.value, item, checked, event })}
      >
        {renderMenuItem ? renderMenuItem(item.label, item) : item.label}
      </DropdownMenuCheckItem>
    )
  }

  return (
    <div role="listbox" className={withClassPrefix('items', { grouped })} style={MENU_STYLE}>
      {visibleRows.map(renderRow)}
    </div>
  )
}
